import { DateTime } from "luxon";
import * as t from "io-ts";
import api, { createDecoder } from "../../api";
import { CargoLocation, addDropoffNoSessionT } from "./types";
import { useAppDispatch } from "../../../redux-store";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { normalizeDecimalInput } from "../../../helpers/decimal-input";
import { getPositionForDriverEventFromWatch } from "../../../helpers/position";
import { selectCargo } from "../../data/route-with-stops/selectors";
import { useSelector } from "react-redux";

export type AddDropoffBody = {
  placeName: string;
  country: string | null;
  postcode: string | null;
  city: string | null;
  address: string | null;
  time: DateTime;
  lm: string;
  revision: string;
  position: { lon: number; lat: number };
  // driverPosition: { lon: number; lat: number; accuracy: number };
};

// Error-reporting so a failed save (e.g. a 400 on a bad loadmeter) is reported
// and shown to the driver instead of escaping as an unhandled rejection.
export const addDropoff = createErrorReportingAsyncThunk(
  "app/add-cargo/addDropoff",
  async (
    {
      cargoId,
      body,
      truckId,
      shouldUseDropoffAsDriverLocation,
    }: {
      cargoId: string;
      body: AddDropoffBody;
      truckId: string;
      shouldUseDropoffAsDriverLocation: boolean;
    },
    thunkApi
  ): Promise<{ stopId: string }> => {
    const driverPosition = shouldUseDropoffAsDriverLocation
      ? {
          ...body.position,
          accuracy: 1,
        }
      : await getPositionForDriverEventFromWatch();

    return await api(thunkApi).post({
      url: `/drivers-api/cargos/${cargoId}/dropoffs-no-session`,
      body: addDropoffNoSessionT.encode({
        ...body,
        lm: normalizeDecimalInput(body.lm),
        driverPosition,
        truckId,
      }),
      decoder: createDecoder(t.strict({ stopId: t.string })),
    });
  }
);

type AddDropoffInput = {
  lm: string;
  time: DateTime;
  dropoffLocation: CargoLocation;
};

export type AddDropoff = (
  input: AddDropoffInput,
  shouldUseDropoffAsDriverLocation: boolean
) => Promise<{ stopId: string }>;

export const useDropoffPage = (input: { cargoId: string; truckId: string }) => {
  const dispatch = useAppDispatch();
  const cargo = useSelector(selectCargo(input.cargoId));
  return {
    cargo,
    addDropoff: async (
      dropoffInput: AddDropoffInput,
      shouldUseDropoffAsDriverLocation: boolean
    ): Promise<{ stopId: string }> => {
      const { lm, time } = dropoffInput;
      if (!cargo) {
        throw new Error("No cargo");
      }
      return dispatch(
        addDropoff({
          ...input,
          body: {
            lm,
            time,
            ...dropoffInput.dropoffLocation,
            revision: cargo.revision,
          },
          shouldUseDropoffAsDriverLocation,
        })
      ).unwrap();
    },
  };
};
